import { memo, useMemo } from 'react';
import styled from 'styled-components';

/** The side panel of the results view (the counting-video design): mode, count, size distribution, count over time. */

const Panel = styled.aside`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.25rem 1.4rem;
	border-radius: 18px;
	border: 1px solid rgba(60, 60, 60, 0.9);
	background: rgba(25, 25, 25, 0.78);
	backdrop-filter: blur(14px);
	color: ${({ theme }) => theme.colors.textColor};
	min-width: 0;
`;

const Title = styled.h3`
	margin: 0;
	font-size: 1.25rem;
	font-weight: 700;
	color: #f5f5f5;
`;

export const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.4rem;
`;

export const Chip = styled.button`
	border: 1px solid
		${({ $active, theme }) =>
			$active ? theme.colors.darkGreen : theme.colors.backgroundAccent};
	background: ${({ $active, theme }) => ($active ? theme.colors.darkGreen : 'transparent')};
	color: ${({ $active }) => ($active ? '#fff' : '#b0b0b0')};
	border-radius: 999px;
	padding: 0.25rem 0.75rem;
	font-size: 0.82rem;
	font-weight: 600;
	cursor: pointer;
	&:hover {
		border-color: ${({ theme }) => theme.colors.green};
	}
`;

const Big = styled.div`
	font-size: 4.2rem;
	line-height: 1;
	font-weight: 800;
	color: #f5f5f5;
	font-variant-numeric: tabular-nums;
`;

const Sub = styled.div`
	color: ${({ theme }) => theme.colors.textColor};
	font-size: 0.95rem;
	margin-top: 0.35rem;
`;

const Note = styled.div`
	color: ${({ $warn }) => ($warn ? '#e0a030' : '#9a9a9a')};
	font-size: 0.78rem;
	margin-top: 0.35rem;
`;

const Rule = styled.hr`
	border: 0;
	border-top: 1px solid rgba(70, 70, 70, 0.9);
	margin: 0;
	opacity: 1;
`;

const Stats = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;
	b {
		display: block;
		font-size: 1.7rem;
		color: #f5f5f5;
		font-variant-numeric: tabular-nums;
	}
	span {
		color: #a0a0a0;
		font-size: 0.8rem;
	}
`;

const H = styled.div`
	font-weight: 600;
	color: #f5f5f5;
	font-size: 0.95rem;
	margin-bottom: 0.5rem;
`;

const Legend = styled.div`
	display: flex;
	gap: 1.2rem;
	font-size: 0.85rem;
	margin-top: 0.4rem;
	i {
		display: inline-block;
		width: 11px;
		height: 11px;
		margin-right: 6px;
		vertical-align: -1px;
		background: ${({ theme }) => theme.colors.green};
	}
	i.calf {
		background: ${({ theme }) => theme.colors.lightGreen};
	}
`;

const Foot = styled.div`
	margin-top: auto;
	color: #9a9a9a;
	font-size: 0.78rem;
`;

// axis value: one decimal below 10 m, whole numbers otherwise
const sizeLabel = (v, unit) => `${unit === 'm' && v < 10 ? v.toFixed(1) : Math.round(v)} ${unit}`;

const Histogram = ({ hist }) => {
	const { calf, adult, hi, unit } = hist;
	const peak = Math.max(1, ...calf.map((c, i) => c + adult[i]));
	const W = 300;
	const Hh = 90;
	const bw = W / calf.length;
	return (
		<svg
			viewBox={`0 0 ${W} ${Hh + 16}`}
			width="100%"
			role="img"
			aria-label={`Body size distribution in ${unit === 'm' ? 'metres' : 'pixels'}`}>
			{calf.map((c, i) => {
				const ha = (Hh * adult[i]) / peak;
				const hc = (Hh * c) / peak;
				return (
					<g key={i}>
						{adult[i] > 0 && (
							<rect
								x={i * bw + 1.5}
								y={Hh - ha}
								width={bw - 3}
								height={ha}
								fill="#20AC6B"
							/>
						)}
						{c > 0 && (
							<rect
								x={i * bw + 1.5}
								y={Hh - ha - hc}
								width={bw - 3}
								height={hc}
								fill="#90d7ad"
							/>
						)}
					</g>
				);
			})}
			<line x1={0} x2={W} y1={Hh} y2={Hh} stroke="#5a5a5a" strokeWidth="1" />
			<text x={0} y={Hh + 13} fill="#9a9a9a" fontSize="10">
				0
			</text>
			{hist.total > 0 && (
				<>
					<text x={W / 2} y={Hh + 13} fill="#9a9a9a" fontSize="10" textAnchor="middle">
						{sizeLabel(hi / 2, unit)}
					</text>
					<text x={W} y={Hh + 13} fill="#9a9a9a" fontSize="10" textAnchor="end">
						{sizeLabel(hi, unit)}
					</text>
				</>
			)}
		</svg>
	);
};

// what the sizes are and how far to trust them
// "reindeer" → "Reindeer"; calves are told apart for reindeer only (the size cut was measured on herds)
const titleCase = w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : '');
export const isReindeer = subject => /^reindeers?$/i.test((subject || 'reindeer').trim());

const sizeNote = (hist, calves) => {
	const calf = calves ? ' Calf-sized: small for the herd at the same distance.' : '';
	if (hist.unit !== 'm')
		return hist.flightWithoutHeight
			? `Body length in pixels: the flight data has no height above the ground to give metres.${calf}`
			: `Body length in pixels: this video has no flight data to give metres.${calf}`;
	const what = hist.across
		? 'Body width across the image, in metres on the ground (the camera is tilted)'
		: 'Body length in metres on the ground';
	const height =
		hist.heightRef === 'takeoff'
			? ', from the flight height above the take-off point (estimate)'
			: ' (estimate)';
	return `${what}${height}.${calf}`;
};

const Sparkline = ({ series, frame }) => {
	const W = 300;
	const Hh = 70;
	const n = series?.length || 0;
	const path = useMemo(() => {
		if (!n) return null;
		const max = Math.max(1, ...series);
		const step = Math.max(1, Math.floor(n / 300));
		const pts = [];
		for (let i = 0; i < n; i += step)
			pts.push([(i / Math.max(1, n - 1)) * W, Hh - (Hh * series[i]) / max]);
		pts.push([W, Hh - (Hh * series[n - 1]) / max]);
		return { line: pts.map(p => p.join(',')).join(' '), max };
	}, [series, n]);
	if (!path) return null;
	const x = (frame / Math.max(1, n - 1)) * W;
	return (
		<svg viewBox={`0 0 ${W} ${Hh + 2}`} width="100%" role="img" aria-label="Count over time">
			<defs>
				<clipPath id="played">
					<rect x="0" y="0" width={x} height={Hh + 2} />
				</clipPath>
			</defs>
			<polyline points={path.line} fill="none" stroke="#3b4a42" strokeWidth="2" />
			<g clipPath="url(#played)">
				<polygon points={`0,${Hh} ${path.line} ${W},${Hh}`} fill="#18462f" />
				<polyline points={path.line} fill="none" stroke="#20AC6B" strokeWidth="2.5" />
			</g>
			<line x1={x} x2={x} y1={0} y2={Hh} stroke="#f5f5f5" strokeWidth="1" opacity="0.6" />
			<line x1={0} x2={W} y1={Hh} y2={Hh} stroke="#5a5a5a" strokeWidth="1" />
		</svg>
	);
};

const SliderRow = styled.label`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin: 0;
	font-size: 0.82rem;
	color: #f5f5f5;
	font-weight: 600;
	input {
		width: 100%;
		accent-color: ${({ theme }) => theme.colors.green};
	}
`;

/** Confidence slider: hides animals whose best detection is below the value; the stored count is the lowest value. */
const Sensitivity = ({ value, min, onChange }) => (
	<SliderRow>
		<span style={{ display: 'flex', justifyContent: 'space-between' }}>
			Sensitivity
			<span style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(value * 100)} %</span>
		</span>
		<input
			type="range"
			min={min}
			max={0.95}
			step={0.01}
			value={value}
			onChange={e => onChange(Number(e.target.value))}
			aria-label="Minimum detection confidence"
			aria-valuetext={`${Math.round(value * 100)} percent`}
		/>
		<span
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				fontWeight: 400,
				fontSize: '0.72rem',
				color: '#9a9a9a',
			}}>
			<span>Find more</span>
			<span>Fewer false alarms</span>
		</span>
	</SliderRow>
);

const MODE_LABEL = { hover: 'Total count', line: 'Line crossing', max: 'Most in view' };

const ChangesBox = styled.div`
	margin-top: 0.6rem;
	padding: 0.55rem 0.65rem;
	border-radius: 10px;
	border: 1px solid rgba(232, 170, 60, 0.45);
	background: rgba(232, 170, 60, 0.07);
	font-size: 0.78rem;
	color: #e8ebe8;
	summary {
		cursor: pointer;
		display: flex;
		justify-content: space-between;
		gap: 0.5rem;
		list-style: none;
	}
	summary::-webkit-details-marker {
		display: none;
	}
	ul {
		margin: 0.45rem 0 0;
		padding: 0;
		list-style: none;
		max-height: 180px;
		overflow: auto;
	}
	li {
		display: flex;
		justify-content: space-between;
		gap: 0.5rem;
		padding: 0.2rem 0;
		color: #c8ccc8;
	}
	button {
		border: 0;
		background: transparent;
		color: #e8aa3c;
		font: inherit;
		cursor: pointer;
		padding: 0;
	}
`;

const EDIT_WORDS = { remove: 'Removed', negative: 'Not a', merge: 'Merged', add: 'Added' };

/** "14 removed · 2 added · 1 merged by you", with the edits newest first and an undo for each. */
const Changes = ({ changes, edits, subject, onUndo, onRetry, onRunAgain }) => {
	const n = changes ? changes.added + changes.removed + changes.merged : 0;
	if (!n || !edits?.length) return null;
	const parts = [
		changes.removed && `${changes.removed} removed`,
		changes.added && `${changes.added} added`,
		changes.merged && `${changes.merged} merged`,
	].filter(Boolean);
	const shown = edits
		.filter(e => ['ready', 'following', 'failed'].includes(e.status))
		.slice()
		.reverse();
	return (
		<ChangesBox>
			<details>
				<summary>
					<span>{parts.join(' · ')} by you</span>
					<button
						type="button"
						onClick={e => {
							e.preventDefault();
							onUndo?.(shown[0].id);
						}}>
						Undo last
					</button>
				</summary>
				<ul>
					{shown.map(e => (
						<li key={e.id}>
							<span>
								{e.type === 'negative' ? `Not a ${subject}` : EDIT_WORDS[e.type]}
								{e.tracks?.length ? ` · #${e.tracks.join(' + #')}` : ''}
								{e.status === 'following' ? ' · following…' : ''}
								{e.status === 'failed'
									? ' · could not follow, counts on this frame only'
									: ''}
							</span>
							<span>
								{e.status === 'failed' && onRetry && (
									<button type="button" onClick={() => onRetry(e.id)}>
										Retry
									</button>
								)}{' '}
								<button
									type="button"
									onClick={() => onUndo?.(e.id)}
									disabled={e.pending}>
									{e.status === 'failed' ? 'Remove' : 'Undo'}
								</button>
							</span>
						</li>
					))}
				</ul>
				{onRunAgain && (
					<button type="button" onClick={onRunAgain}>
						Run again with what I taught it
					</button>
				)}
			</details>
		</ChangesBox>
	);
};

/** The assistant's proposals, to accept one by one or all at once; nothing counts until accepted. */
const Proposals = ({ proposals, subject, fps, onAccept, onDismiss }) => {
	if (!proposals?.length) return null;
	const at = f => {
		const sec = Math.round((f || 0) / (fps || 30));
		return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
	};
	return (
		<ChangesBox>
			<details open>
				<summary>
					<span>From the assistant · {proposals.length} to review</span>
					<button
						type="button"
						onClick={e => {
							e.preventDefault();
							proposals.forEach(pr => onAccept?.(pr.id));
						}}>
						Accept all
					</button>
				</summary>
				<ul>
					{proposals.map(pr => (
						<li key={pr.id}>
							<span>
								{pr.type === 'add'
									? `Missed ${subject} at ${at(pr.frame)}`
									: `Not a ${subject}${pr.reason ? ` (${pr.reason})` : ''} · #${pr.tracks?.[0]}`}
							</span>
							<span>
								<button type="button" onClick={() => onAccept?.(pr.id)}>
									Accept
								</button>{' '}
								<button type="button" onClick={() => onDismiss?.(pr.id)}>
									Dismiss
								</button>
							</span>
						</li>
					))}
				</ul>
			</details>
		</ChangesBox>
	);
};

const CountPanel = ({
	modes,
	mode,
	onModeChange,
	count,
	subtitle,
	note,
	noteWarn,
	inView,
	maxInView,
	hist,
	series,
	frame,
	modelLabel,
	sensitivity,
	subject = 'reindeer',
	changes = null,
	edits = null,
	onUndo = null,
	onAccept = null,
	onRetry = null,
	fps = 30,
	onRunAgain = null,
}) => (
	<Panel>
		<Title>{titleCase((subject || 'reindeer').trim())} count</Title>
		<Chips role="tablist" aria-label="Counting mode">
			{modes.map(m => (
				<Chip
					key={m}
					type="button"
					role="tab"
					aria-selected={m === mode}
					$active={m === mode}
					onClick={() => onModeChange(m)}>
					{MODE_LABEL[m]}
				</Chip>
			))}
		</Chips>
		<div>
			<Big>{count ?? '—'}</Big>
			<Sub>{subtitle}</Sub>
			{note && <Note $warn={noteWarn}>{note}</Note>}
			<Changes
				changes={changes}
				edits={edits}
				subject={subject || 'object'}
				onUndo={onUndo}
				onRetry={onRetry}
				onRunAgain={onRunAgain}
			/>
			<Proposals
				proposals={(edits || []).filter(e => e.status === 'proposed')}
				subject={subject || 'object'}
				fps={fps}
				onAccept={onAccept}
				onDismiss={onUndo}
			/>
		</div>
		{sensitivity?.available && (
			<>
				<Rule />
				<Sensitivity {...sensitivity} />
			</>
		)}
		<Rule />
		<Stats>
			<div>
				<b>{inView}</b>
				<span>In view now</span>
			</div>
			<div>
				<b>{maxInView}</b>
				<span>Most in view at once</span>
			</div>
		</Stats>
		<Rule />
		<div>
			<H>Body size of what was counted</H>
			<Histogram
				hist={
					isReindeer(subject)
						? hist
						: {
								...hist,
								calf: hist.calf.map(() => 0),
								adult: hist.calf.map((c, i) => c + hist.adult[i]),
							}
				}
			/>
			{isReindeer(subject) ? (
				<Legend>
					<span>
						<i className="calf" />
						{hist.nCalf} calf-sized
					</span>
					<span>
						<i />
						{hist.total - hist.nCalf} adult-sized
					</span>
				</Legend>
			) : (
				<Legend>
					<span>
						<i />
						{hist.total} counted
					</span>
				</Legend>
			)}
			<Note>{sizeNote(hist, isReindeer(subject))}</Note>
		</div>
		<Rule />
		<div>
			<H>Count over time</H>
			<Sparkline series={series} frame={frame} />
		</div>
		<Foot>{modelLabel}</Foot>
	</Panel>
);

export default memo(CountPanel);
